import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ClipboardList, MessagesSquare, BadgeCheck, ShieldCheck, Wallet, Scale, Handshake } from "lucide-react";
import { api } from "@/lib/api";
import { useCatalog } from "@/lib/useCatalog";
import { SearchBar } from "@/components/SearchBar";
import { ProviderCard } from "@/components/ProviderCard";
import { CategoryIcon } from "@/components/CategoryIcon";

const HERO = "https://static.prod-images.emergentagent.com/jobs/e721ece0-0dd3-437f-9af4-b69114055a15/images/d2f1001f7fdccbd990e40d050839e6294ee80822a2fd00c82a0cc205a6e041f6.jpeg";
const POPULAR = [["service-ac", "Service AC"], ["cleaning-rumah", "Cleaning Rumah"], ["renovasi-rumah", "Renovasi Rumah"], ["catering", "Catering"]];

const Hero = ({ cities }) => (
  <section className="container-page grid items-center gap-12 pb-20 pt-12 lg:grid-cols-[1.05fr_1fr] lg:pt-20">
    <div className="animate-fade-up">
      <p className="eyebrow">Marketplace jasa Indonesia</p>
      <h1 className="mt-5 text-4xl font-extrabold leading-[1.08] tracking-tight text-stone-900 sm:text-5xl lg:text-6xl">
        Urusan rumah & acara, <span className="text-teal-700">beres tuntas.</span>
      </h1>
      <p className="mt-6 max-w-lg text-base leading-relaxed text-stone-600 md:text-lg">
        Ceritakan kebutuhan Anda sekali, terima hingga 3 penawaran dari penyedia jasa di kota Anda, lalu pilih yang paling cocok.
      </p>
      <div className="mt-8 max-w-xl"><SearchBar cities={cities} /></div>
      <div className="mt-5 flex flex-wrap items-center gap-2 text-sm text-stone-500">
        <span>Populer:</span>
        {POPULAR.map(([slug, name]) => (
          <Link key={slug} to={`/kategori/${slug}`} className="chip hover:border-teal-600 hover:text-teal-800" data-testid={`popular-chip-${slug}`}>{name}</Link>
        ))}
      </div>
    </div>
    <div className="relative animate-fade-up [animation-delay:120ms]">
      <div className="grain relative overflow-hidden rounded-[2rem]">
        <img src={HERO} alt="Teknisi AC sedang bekerja di rumah" className="h-[420px] w-full object-cover lg:h-[520px]" />
      </div>
      <div className="absolute -bottom-6 left-6 right-6 flex items-center gap-4 rounded-2xl border border-white/60 bg-white/85 p-4 shadow-lg backdrop-blur-md sm:right-auto sm:w-80">
        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-teal-700 text-white"><Handshake className="h-5 w-5" /></span>
        <div>
          <p className="text-sm font-bold text-stone-900">Gratis untuk pelanggan</p>
          <p className="text-xs text-stone-500">Tanpa komitmen sampai Anda memilih.</p>
        </div>
      </div>
    </div>
  </section>
);

const GroupTile = ({ group, big }) => (
  <div className={`card-soft group relative overflow-hidden ${big ? "lg:col-span-3" : "lg:col-span-2"}`} data-testid={`category-group-${group.slug}`}>
    <div className={`relative overflow-hidden ${big ? "h-52" : "h-40"}`}>
      <img src={group.image} alt={group.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
      <div className="absolute inset-0 bg-gradient-to-t from-stone-900/55 to-transparent" />
      <div className="absolute bottom-4 left-5 text-white">
        <h3 className="text-xl font-bold">{group.name}</h3>
        <p className="text-sm text-white/85">{group.description}</p>
      </div>
    </div>
    <ul className="grid grid-cols-1 gap-1 p-4 sm:grid-cols-2">
      {group.subcategories.map((s) => (
        <li key={s.slug}>
          <Link to={`/kategori/${s.slug}`} data-testid={`subcategory-link-${s.slug}`}
            className="flex items-center gap-2.5 rounded-xl px-2.5 py-2 text-sm font-medium text-stone-700 transition-colors hover:bg-teal-50 hover:text-teal-800">
            <CategoryIcon name={s.icon} className="h-4 w-4 text-teal-700" /> {s.name}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

const STEPS = [
  [ClipboardList, "Ceritakan kebutuhan", "Pilih jenis jasa, jawab beberapa pertanyaan singkat, dan tentukan lokasi serta jadwal."],
  [MessagesSquare, "Terima hingga 3 penawaran", "Penyedia yang sesuai kota dan keahlian mengirim harga, estimasi, dan pesan untuk Anda."],
  [BadgeCheck, "Pilih & hubungi", "Bandingkan, ngobrol lewat chat, lalu pilih satu penawaran dan lanjutkan via WhatsApp."],
];

const HowItWorks = () => (
  <section className="bg-white py-20 lg:py-24" id="cara-kerja" data-testid="how-it-works-section">
    <div className="container-page">
      <p className="eyebrow">Cara Kerja</p>
      <h2 className="mt-3 max-w-xl text-3xl font-bold tracking-tight text-stone-900 sm:text-4xl">Tiga langkah, tanpa harus menelepon satu per satu.</h2>
      <div className="mt-12 grid gap-6 md:grid-cols-3">
        {STEPS.map(([Icon, title, desc], i) => (
          <div key={title} className="relative rounded-2xl border border-stone-200 bg-stone-50/60 p-7" data-testid={`how-step-${i + 1}`}>
            <span className="absolute right-6 top-5 text-5xl font-extrabold text-stone-200">{i + 1}</span>
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-teal-700 text-white"><Icon className="h-5 w-5" /></span>
            <h3 className="mt-6 text-lg font-bold text-stone-900">{title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-stone-500">{desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Trust = ({ stats }) => (
  <section className="container-page py-20 lg:py-24" data-testid="trust-section">
    <div className="grid gap-12 rounded-[2rem] bg-teal-900 p-8 text-white sm:p-12 lg:grid-cols-[1fr_1.2fr]">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-200">Kenapa Tuntas</p>
        <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">Dibangun untuk rasa aman kedua belah pihak.</h2>
        <div className="mt-10 grid grid-cols-3 gap-4">
          {stats.map(([n, label]) => (
            <div key={label} data-testid={`trust-stat-${label.replace(/\s/g, "-").toLowerCase()}`}>
              <p className="text-3xl font-extrabold">{n}</p>
              <p className="mt-1 text-xs text-teal-100/80">{label}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {[
          [ShieldCheck, "Penyedia terverifikasi", "Lencana Terverifikasi untuk penyedia yang telah melalui pemeriksaan tim kami."],
          [Wallet, "Gratis untuk pelanggan", "Minta penawaran sebanyak yang Anda butuhkan tanpa biaya."],
          [Scale, "Harga transparan", "Bandingkan harga dan estimasi pengerjaan secara berdampingan."],
          [MessagesSquare, "Kontak terlindungi", "Nomor WhatsApp hanya dibagikan setelah Anda memilih penawaran."],
        ].map(([Icon, t, d]) => (
          <div key={t} className="rounded-2xl bg-white/5 p-5 ring-1 ring-white/10">
            <Icon className="h-5 w-5 text-teal-200" />
            <h3 className="mt-4 font-semibold">{t}</h3>
            <p className="mt-1.5 text-sm leading-relaxed text-teal-100/75">{d}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default function Home() {
  const { groups, cities, subcategories } = useCatalog();
  const [featured, setFeatured] = useState([]);
  const [providerCount, setProviderCount] = useState(0);

  useEffect(() => {
    api.get("/providers", { params: { featured: true, limit: 6 } }).then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/providers", { params: { limit: 100 } }).then((r) => setProviderCount(r.data.length)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <Hero cities={cities} />
      <section className="container-page py-16" id="kategori" data-testid="category-groups-section">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <p className="eyebrow">Kategori</p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-stone-900 sm:text-4xl">Semua kebutuhan, satu tempat.</h2>
          </div>
          <p className="max-w-sm text-sm text-stone-500">{subcategories.length} jenis jasa dalam 5 kelompok, dari service AC sampai pembuatan website.</p>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-6">
          {groups.map((g, i) => <GroupTile key={g.slug} group={g} big={i < 2} />)}
        </div>
      </section>
      <HowItWorks />
      <section className="container-page py-20" data-testid="featured-providers-section">
        <div className="flex items-end justify-between gap-4">
          <div>
            <p className="eyebrow">Penyedia Pilihan</p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-stone-900 sm:text-4xl">Penyedia terverifikasi</h2>
          </div>
          <Link to="/cari?q=jasa" className="hidden items-center gap-1 text-sm font-semibold text-teal-700 hover:text-teal-900 sm:flex" data-testid="featured-see-all">
            Jelajahi <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {featured.map((p) => <ProviderCard key={p.slug} provider={p} />)}
        </div>
      </section>
      <Trust stats={[[subcategories.length, "Jenis jasa"], [cities.length, "Kota"], [providerCount, "Penyedia aktif"]]} />
      <section className="container-page" data-testid="provider-cta-section">
        <div className="card-soft flex flex-col items-start justify-between gap-6 p-8 sm:flex-row sm:items-center sm:p-10">
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-stone-900">Punya keahlian? Dapatkan pelanggan baru.</h2>
            <p className="mt-2 text-stone-500">Daftar gratis dan dapatkan 5 kredit awal untuk mengirim penawaran.</p>
          </div>
          <Link to="/daftar?peran=penyedia" data-testid="provider-cta-button"
            className="inline-flex items-center gap-2 rounded-full bg-stone-900 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-teal-800">
            Daftar sebagai penyedia <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
